import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const ClientProgressNav = ({ 
  currentStep = 1, 
  totalSteps = 8, 
  exerciseName = "Seated Leg Extensions",
  sessionTime = "00:00",
  canGoBack = true,
  canGoForward = true,
  onBack,
  onForward,
  onPause,
  onEmergency
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPaused, setIsPaused] = useState(false);
  const [showEmergencyConfirm, setShowEmergencyConfirm] = useState(false);

  const progressPercentage = (currentStep / totalSteps) * 100;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (currentStep > 1) {
      // Default back behavior
      console.log('Going back to previous exercise');
    }
  };

  const handleForward = () => {
    if (onForward) {
      onForward();
    } else if (currentStep < totalSteps) {
      // Default forward behavior
      console.log('Going to next exercise');
    }
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
    if (onPause) {
      onPause(!isPaused);
    }
  };

  const handleEmergency = () => {
    if (showEmergencyConfirm) {
      if (onEmergency) {
        onEmergency();
      } else {
        // Default emergency behavior
        navigate('/training-plan-selection');
      }
      setShowEmergencyConfirm(false);
    } else {
      setShowEmergencyConfirm(true);
      // Auto-hide confirmation after 5 seconds
      setTimeout(() => setShowEmergencyConfirm(false), 5000);
    }
  };

  const handleHome = () => {
    navigate('/training-plan-selection');
  };

  const isExerciseExecution = location?.pathname === '/exercise-execution';

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border shadow-lg"
      role="navigation"
      aria-label="Exercise progress navigation"
    >
      {/* Progress Bar */}
      {isExerciseExecution && (
        <div className="w-full h-2 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={currentStep}
            aria-valuemin={1}
            aria-valuemax={totalSteps}
            aria-label={`Exercise ${currentStep} of ${totalSteps}`}
          />
        </div>
      )}

      <div className="flex items-center justify-between p-4 touch-spacing">
        {/* Left Section - Back/Home */}
        <div className="flex items-center space-x-3">
          {isExerciseExecution ? (
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              disabled={!canGoBack}
              className="breathing-focus haptic-button min-w-touch min-h-touch"
              aria-label="Previous exercise"
            >
              <Icon name="ChevronLeft" size={24} />
              <span className="ml-2 hidden sm:inline">Back</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="lg"
              onClick={handleHome}
              className="breathing-focus haptic-button min-w-touch min-h-touch"
              aria-label="Return to home"
            >
              <Icon name="Home" size={24} />
              <span className="ml-2 hidden sm:inline">Home</span>
            </Button>
          )}
        </div>

        {/* Center Section - Exercise Info */}
        <div className="flex-1 text-center px-4">
          {isExerciseExecution ? (
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                {exerciseName}
              </h2>
              <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                <span className="font-mono text-base">{sessionTime}</span>
                <span>•</span>
                <span>Step {currentStep} of {totalSteps}</span>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                FitTrainer Pro
              </h2>
              <p className="text-sm text-muted-foreground">
                Your fitness journey starts here
              </p>
            </div>
          )}
        </div>

        {/* Right Section - Controls */}
        <div className="flex items-center space-x-3">
          {isExerciseExecution && (
            <>
              {/* Pause/Resume Button */}
              <Button
                variant={isPaused ? "default" : "secondary"}
                size="lg"
                onClick={handlePause}
                className="breathing-focus haptic-button min-w-touch min-h-touch"
                aria-label={isPaused ? "Resume exercise" : "Pause exercise"}
              >
                <Icon name={isPaused ? "Play" : "Pause"} size={24} />
                <span className="ml-2 hidden sm:inline">
                  {isPaused ? "Resume" : "Pause"}
                </span>
              </Button>

              {/* Emergency Button */}
              <Button
                variant={showEmergencyConfirm ? "destructive" : "outline"}
                size="lg"
                onClick={handleEmergency}
                className="breathing-focus haptic-button min-w-touch min-h-touch"
                aria-label={showEmergencyConfirm ? "Confirm emergency stop" : "Emergency stop"}
              >
                <Icon name="AlertTriangle" size={24} />
                <span className="ml-2 hidden sm:inline">
                  {showEmergencyConfirm ? "Confirm" : "Help"}
                </span>
              </Button>
            </>
          )}

          {/* Forward/Next Button */}
          {isExerciseExecution ? (
            <Button
              variant="default"
              size="lg"
              onClick={handleForward}
              disabled={!canGoForward}
              className="breathing-focus haptic-button min-w-touch min-h-touch"
              aria-label="Next exercise"
            >
              <span className="mr-2 hidden sm:inline">Next</span>
              <Icon name="ChevronRight" size={24} />
            </Button>
          ) : (
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/exercise-execution')}
              className="breathing-focus haptic-button min-w-touch min-h-touch"
              aria-label="Start training"
            >
              <span className="mr-2 hidden sm:inline">Start</span>
              <Icon name="Play" size={24} />
            </Button>
          )}
        </div>
      </div>

      {/* Emergency Confirmation Banner */}
      {showEmergencyConfirm && (
        <div className="bg-destructive/10 border-t border-destructive/20 p-4">
          <div className="flex items-center justify-center space-x-4">
            <Icon name="AlertTriangle" size={20} className="text-destructive" />
            <p className="text-destructive font-medium">
              Tap "Confirm" again to stop your session and call for help
            </p>
          </div>
        </div>
      )}

      {/* Pause Overlay Info */}
      {isPaused && isExerciseExecution && (
        <div className="bg-warning/10 border-t border-warning/20 p-4">
          <div className="flex items-center justify-center space-x-4">
            <Icon name="Pause" size={20} className="text-warning" />
            <p className="text-warning font-medium">
              Session paused - Take your time, tap Resume when ready
            </p>
          </div>
        </div>
      )}
    </nav>
  );
};

export default ClientProgressNav;